import { useState, type FormEvent, type ReactNode } from 'react';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import { useLookups, CLIENT_PAYMENT_TYPES, todayLocal } from '../lib/lookups';
import {
  useProject,
  useAddPayment,
  useAddPurchase,
  useAddUsage,
  useAddLabour,
  useAddExpense,
  useAddTransfer,
} from '../lib/project';
import { useUpdateEntry } from '../lib/project-edit';
import { Drawer, Field, useToast, Select } from './ui';
import {
  Wallet,
  ShoppingBag,
  Box,
  HardHat,
  Receipt,
  Repeat,
  Building2,
  User,
  Calendar,
  DollarSign,
  Trash2,
  Sparkles,
} from 'lucide-react';

export type EntryKind = 'payment' | 'purchase' | 'usage' | 'labour' | 'expense' | 'transfer';

export const ENTRY_META: Record<EntryKind, { label: string; title: string; hint: string }> = {
  payment: { label: 'Add Payment', title: 'Add Client Payment', hint: 'Money received from the client for this project' },
  purchase: { label: 'Supplier Purchase', title: 'Add Supplier Purchase', hint: 'Material bought for this site' },
  usage: { label: 'Add Material', title: 'Record Material Used', hint: 'Material consumed on site' },
  labour: { label: 'Add Attendance', title: 'Subcontractor Attendance', hint: 'Trade muster roll & worker headcount' },
  expense: { label: 'Add Expense', title: 'Add Site Expense', hint: 'Vehicle, driver and other site small spend' },
  transfer: { label: 'Site Transfer', title: 'Transfer Material Out', hint: 'Send material to another site' },
};

/** Rupee amounts: digits with at most two decimals, commas allowed. */
const MONEY = /^\d+(\.\d{1,2})?$/;
const cleanMoney = (s: string) => s.replace(/[₹,\s]/g, '');

/** A ledger row being corrected. Loose because the six tables differ. */
export type ExistingEntry = Record<string, unknown> & { id: string };

export default function EntryDrawer({
  kind,
  projectId,
  existing,
  onClose,
  onDelete,
}: {
  kind: EntryKind;
  projectId: string;
  existing?: ExistingEntry;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const meta = ENTRY_META[kind];
  const props = {
    projectId,
    onClose,
    existing,
    onDelete,
    title: existing ? EDIT_TITLE[kind] : meta.title,
    hint: existing ? 'Correcting an entry that has already been counted in ledgers' : meta.hint,
  };
  switch (kind) {
    case 'payment':
      return <PaymentForm {...props} />;
    case 'purchase':
      return <PurchaseForm {...props} />;
    case 'usage':
      return <UsageForm {...props} />;
    case 'labour':
      return <LabourForm {...props} />;
    case 'expense':
      return <ExpenseForm {...props} />;
    case 'transfer':
      return <TransferForm {...props} />;
  }
}

const EDIT_TITLE: Record<EntryKind, string> = {
  payment: 'Edit Payment',
  purchase: 'Edit Purchase',
  usage: 'Edit Material Used',
  labour: 'Edit Attendance',
  expense: 'Edit Expense',
  transfer: 'Edit Transfer',
};

interface FormProps {
  projectId: string;
  onClose: () => void;
  title: string;
  hint: string;
  existing?: ExistingEntry;
  onDelete?: () => void;
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const rupees = (v: unknown) =>
  typeof v === 'number' ? String(v / 100) : v ? String(Number(v) / 100) : '';

function useEntryForm<T extends Record<string, string>>(
  initial: T,
  validate: (v: T) => Record<string, string>,
  submit: (v: T) => Promise<unknown>,
  onClose: () => void,
  successMessage: string,
  editing = false,
) {
  const [form, setForm] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const actor = useSession((s) => s.actor);

  const set = (k: keyof T) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next = validate(form);
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await submit(form);
      toast.show(
        editing
          ? 'Entry updated successfully'
          : isAdmin(actor)
          ? successMessage
          : `${successMessage} — sent for approval`,
      );
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save this entry', true);
    } finally {
      setBusy(false);
    }
  }

  return { form, setForm, errors, busy, set, onSubmit };
}

function LookupSelect({
  value,
  onChange,
  options,
  placeholder,
  invalid,
  disabled,
}: {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  options: Array<{ id: string; name: string }> | undefined;
  placeholder: string;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const selectOptions = [
    { value: '', label: disabled ? 'Loading…' : placeholder },
    ...(options?.map((o) => ({ value: o.id, label: o.name })) ?? []),
  ];
  return (
    <Select
      value={value}
      onChange={(val) => onChange({ target: { value: val } })}
      options={selectOptions}
      placeholder={placeholder}
      disabled={disabled}
      className={invalid ? 'border-rose-500' : ''}
    />
  );
}

function Wrap({
  title,
  hint,
  onClose,
  onSubmit,
  busy,
  editing,
  onDelete,
  children,
}: {
  title: string;
  hint: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  busy: boolean;
  editing?: boolean;
  onDelete?: () => void;
  children: ReactNode;
}) {
  return (
    <Drawer
      title={title}
      description={hint}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={editing ? 'Save Changes' : 'Save Entry'}
      busy={busy}
      onDelete={onDelete}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">{children}</div>
    </Drawer>
  );
}

function EntryContext({ client, project }: { client?: string; project?: string }) {
  return (
    <div className="col-span-1 sm:col-span-2 p-3 rounded-[8px] bg-slate-50/80 border border-slate-200/80 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-slate-400 shrink-0" />
        <div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Client</div>
          <div className="text-xs font-semibold text-slate-800">{client ?? '—'}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-right">
        <div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Project</div>
          <div className="text-xs font-semibold text-sky-700">{project ?? '—'}</div>
        </div>
        <Building2 className="w-4 h-4 text-sky-500 shrink-0" />
      </div>
    </div>
  );
}

/* ------------------------------- Payment Form ------------------------------- */

function PaymentForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const actor = useSession((s) => s.actor);
  if (!isAdmin(actor)) {
    return null;
  }
  const { data: project } = useProject(projectId);
  const add = useAddPayment(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.paid_on),
          payment_type: str(existing.payment_type),
          amountRupees: rupees(existing.amount_paise),
          reference_no: str(existing.reference_no),
          remarks: str(existing.remarks),
        }
      : { date: todayLocal(), payment_type: 'milestone', amountRupees: '', reference_no: '', remarks: '' },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.date) e.date = 'Date is required';
      const amt = cleanMoney(v.amountRupees);
      if (!amt) e.amountRupees = 'Amount is required';
      else if (!MONEY.test(amt)) e.amountRupees = 'Enter a valid amount';
      else if (Number(amt) <= 0) e.amountRupees = 'Amount must be more than zero';
      return e;
    },
    (v) => {
      const values = { ...v, amountRupees: cleanMoney(v.amountRupees) };
      return existing
        ? update.mutateAsync({ kind: 'payment', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Payment recorded',
    !!existing,
  );

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <EntryContext client={project?.clientName} project={project?.name} />

      <Field label="Date Received" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Payment Classification">
        <Select
          value={f.form.payment_type}
          onChange={(val) => f.set('payment_type')({ target: { value: val } })}
          options={CLIENT_PAYMENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        />
      </Field>

      <Field label="Amount Received (₹)" error={f.errors.amountRupees}>
        <input
          value={f.form.amountRupees}
          onChange={f.set('amountRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.amountRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-bold"
        />
      </Field>

      <Field label="Reference / Cheque / UTR No." optional>
        <input
          value={f.form.reference_no}
          onChange={f.set('reference_no')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}

/* ------------------------------- Purchase Form ------------------------------- */

function PurchaseForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const { data: lk, isLoading } = useLookups();
  const add = useAddPurchase(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.purchased_on),
          material_id: str(existing.material_id),
          supplier_id: str(existing.supplier_id),
          unit_id: str(existing.unit_id),
          quantity: str(existing.quantity),
          rateRupees: rupees(existing.rate_paise),
          advanceRupees: rupees(existing.advance_paid_paise),
          invoice_no: str(existing.invoice_no),
          remarks: str(existing.remarks),
        }
      : {
          date: todayLocal(),
          material_id: '',
          supplier_id: '',
          unit_id: '',
          quantity: '',
          rateRupees: '',
          advanceRupees: '',
          invoice_no: '',
          remarks: '',
        },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.material_id) e.material_id = 'Select a material';
      if (!v.supplier_id) e.supplier_id = 'Select a supplier';
      if (!v.quantity || Number(v.quantity) <= 0) e.quantity = 'Enter a quantity';
      const rate = cleanMoney(v.rateRupees);
      if (!rate || !MONEY.test(rate)) e.rateRupees = 'Enter a valid rate';
      if (v.advanceRupees && !MONEY.test(cleanMoney(v.advanceRupees)))
        e.advanceRupees = 'Enter a valid amount';
      return e;
    },
    (v) => {
      const values = {
        ...v,
        rateRupees: cleanMoney(v.rateRupees),
        advanceRupees: v.advanceRupees ? cleanMoney(v.advanceRupees) : '',
      };
      return existing
        ? update.mutateAsync({ kind: 'purchase', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Purchase recorded',
    !!existing,
  );

  const qty = Number(f.form.quantity);
  const rate = Number(cleanMoney(f.form.rateRupees));
  const total =
    Number.isFinite(qty) && Number.isFinite(rate) && qty > 0 && rate > 0
      ? (qty * rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : null;

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <Field label="Delivery Date" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Supplier" error={f.errors.supplier_id}>
        <LookupSelect
          value={f.form.supplier_id}
          onChange={f.set('supplier_id')}
          options={lk?.suppliers}
          placeholder="Choose a supplier"
          invalid={!!f.errors.supplier_id}
          disabled={isLoading}
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Material" error={f.errors.material_id} span2>
          <LookupSelect
            value={f.form.material_id}
            onChange={f.set('material_id')}
            options={lk?.materials}
            placeholder="Choose a material"
            invalid={!!f.errors.material_id}
            disabled={isLoading}
          />
        </Field>
      </div>

      <Field label="Quantity" error={f.errors.quantity}>
        <input
          value={f.form.quantity}
          onChange={f.set('quantity')}
          inputMode="decimal"
          aria-invalid={!!f.errors.quantity}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field label="Unit" optional>
        <LookupSelect
          value={f.form.unit_id}
          onChange={f.set('unit_id')}
          options={lk?.units}
          placeholder="Choose a unit"
          disabled={isLoading}
        />
      </Field>

      <Field label="Rate (₹)" error={f.errors.rateRupees} hint={total ? `Calculated total: ₹${total}` : undefined}>
        <input
          value={f.form.rateRupees}
          onChange={f.set('rateRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.rateRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field label="Advance Paid (₹)" error={f.errors.advanceRupees} optional>
        <input
          value={f.form.advanceRupees}
          onChange={f.set('advanceRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.advanceRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Invoice / Delivery Challan No." optional span2>
          <input
            value={f.form.invoice_no}
            onChange={f.set('invoice_no')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
          />
        </Field>
      </div>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}

/* ------------------------------- Usage Form ------------------------------- */

function UsageForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const { data: lk, isLoading } = useLookups();
  const add = useAddUsage(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.used_on),
          material_id: str(existing.material_id),
          unit_id: str(existing.unit_id),
          quantity: str(existing.quantity),
          activity: str(existing.activity),
          remarks: str(existing.remarks),
        }
      : { date: todayLocal(), material_id: '', unit_id: '', quantity: '', activity: '', remarks: '' },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.material_id) e.material_id = 'Select a material';
      if (!v.quantity || Number(v.quantity) <= 0) e.quantity = 'Enter a quantity';
      return e;
    },
    (v) => {
      const values = v;
      return existing
        ? update.mutateAsync({ kind: 'usage', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Material usage recorded',
    !!existing,
  );

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <Field label="Date Used" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Material" error={f.errors.material_id}>
        <LookupSelect
          value={f.form.material_id}
          onChange={f.set('material_id')}
          options={lk?.materials}
          placeholder="Choose a material"
          invalid={!!f.errors.material_id}
          disabled={isLoading}
        />
      </Field>

      <Field label="Quantity Consumed" error={f.errors.quantity}>
        <input
          value={f.form.quantity}
          onChange={f.set('quantity')}
          inputMode="decimal"
          aria-invalid={!!f.errors.quantity}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field label="Unit" optional>
        <LookupSelect
          value={f.form.unit_id}
          onChange={f.set('unit_id')}
          options={lk?.units}
          placeholder="Choose a unit"
          disabled={isLoading}
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Work Activity / Milestone" optional span2>
          <input
            value={f.form.activity}
            onChange={f.set('activity')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}

/* ------------------------------- Labour Form ------------------------------- */

function LabourForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const { data: lk, isLoading } = useLookups();
  const add = useAddLabour(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.worked_on),
          subcontractor_id: str(existing.subcontractor_id),
          work_type_id: str(existing.work_type_id),
          worker_count: str(existing.worker_count),
          ratePerWorkerRupees: rupees(existing.rate_per_worker_paise),
          totalRupees: rupees(existing.total_amount_paise),
          remarks: str(existing.remarks),
        }
      : {
          date: todayLocal(),
          subcontractor_id: '',
          work_type_id: '',
          worker_count: '',
          ratePerWorkerRupees: '',
          totalRupees: '',
          remarks: '',
        },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.subcontractor_id) e.subcontractor_id = 'Select a subcontractor';
      if (!v.work_type_id) e.work_type_id = 'Select a trade';
      const n = Number(v.worker_count);
      if (!v.worker_count || !Number.isInteger(n) || n <= 0)
        e.worker_count = 'Enter a whole number of workers';
      if (v.ratePerWorkerRupees && !MONEY.test(cleanMoney(v.ratePerWorkerRupees)))
        e.ratePerWorkerRupees = 'Enter a valid rate';
      if (v.totalRupees && !MONEY.test(cleanMoney(v.totalRupees)))
        e.totalRupees = 'Enter a valid amount';
      return e;
    },
    (v) => {
      const values = {
        ...v,
        ratePerWorkerRupees: v.ratePerWorkerRupees ? cleanMoney(v.ratePerWorkerRupees) : '',
        totalRupees: v.totalRupees ? cleanMoney(v.totalRupees) : '',
      };
      return existing
        ? update.mutateAsync({ kind: 'labour', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Attendance recorded',
    !!existing,
  );

  const count = Number(f.form.worker_count);
  const rate = Number(cleanMoney(f.form.ratePerWorkerRupees));
  const derived =
    count > 0 && rate > 0 && !f.form.totalRupees
      ? (count * rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : null;

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <Field label="Attendance Date" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Subcontractor" error={f.errors.subcontractor_id}>
        <LookupSelect
          value={f.form.subcontractor_id}
          onChange={f.set('subcontractor_id')}
          options={lk?.subcontractors}
          placeholder="Choose a subcontractor"
          invalid={!!f.errors.subcontractor_id}
          disabled={isLoading}
        />
      </Field>

      <Field label="Trade / Work Type" error={f.errors.work_type_id}>
        <LookupSelect
          value={f.form.work_type_id}
          onChange={f.set('work_type_id')}
          options={lk?.workTypes}
          placeholder="Choose a trade"
          invalid={!!f.errors.work_type_id}
          disabled={isLoading}
        />
      </Field>

      <Field label="Number of Workers" error={f.errors.worker_count} hint={!f.errors.worker_count ? 'e.g. 10 masons' : undefined}>
        <input
          value={f.form.worker_count}
          onChange={f.set('worker_count')}
          inputMode="numeric"
          aria-invalid={!!f.errors.worker_count}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field label="Rate per Worker (₹)" error={f.errors.ratePerWorkerRupees} optional>
        <input
          value={f.form.ratePerWorkerRupees}
          onChange={f.set('ratePerWorkerRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.ratePerWorkerRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field
        label="Total Amount (₹)"
        error={f.errors.totalRupees}
        optional
        hint={derived ? `Auto-calculated ₹${derived} unless overridden` : undefined}
      >
        <input
          value={f.form.totalRupees}
          onChange={f.set('totalRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.totalRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}

/* ------------------------------- Expense Form ------------------------------- */

function ExpenseForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const { data: lk, isLoading } = useLookups();
  const add = useAddExpense(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.spent_on),
          title: str(existing.title),
          category_id: str(existing.category_id),
          amountRupees: rupees(existing.amount_paise),
          paid_to: str(existing.paid_to),
          bill_no: str(existing.bill_no),
          remarks: str(existing.remarks),
        }
      : { date: todayLocal(), title: '', category_id: '', amountRupees: '', paid_to: '', bill_no: '', remarks: '' },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.title.trim()) e.title = 'Name of the expense is required';
      const amt = cleanMoney(v.amountRupees);
      if (!amt) e.amountRupees = 'Amount is required';
      else if (!MONEY.test(amt) || Number(amt) <= 0) e.amountRupees = 'Enter a valid amount';
      return e;
    },
    (v) => {
      const values = { ...v, amountRupees: cleanMoney(v.amountRupees) };
      return existing
        ? update.mutateAsync({ kind: 'expense', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Expense recorded',
    !!existing,
  );

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <Field label="Expense Date" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Category" optional>
        <Select
          value={f.form.category_id}
          onChange={(val) => f.set('category_id')({ target: { value: val } })}
          disabled={isLoading}
          placeholder={isLoading ? 'Loading…' : 'Choose a category'}
          options={[
            { value: '', label: isLoading ? 'Loading…' : 'Choose a category' },
            ...(lk?.expenseCategories?.map((c) => ({ value: c.id, label: c.name })) ?? []),
            { value: '__other__', label: 'Others (Specify below)…' },
          ]}
        />
        {f.form.category_id === '__other__' && (
          <input
            className="w-full px-2 py-1 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 mt-1"
            onChange={(e) => {
              const custom = e.target.value;
              f.setForm((prev) => ({
                ...prev,
                remarks: prev.remarks.includes('[Category: ')
                  ? prev.remarks.replace(/\[Category: .*?\]/, `[Category: ${custom}]`)
                  : `[Category: ${custom}] ${prev.remarks}`.trim(),
              }));
            }}
          />
        )}
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Name / Purpose of Expense" error={f.errors.title} span2>
          <input
            value={f.form.title}
            onChange={f.set('title')}
            aria-invalid={!!f.errors.title}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>

      <Field label="Amount (₹)" error={f.errors.amountRupees}>
        <input
          value={f.form.amountRupees}
          onChange={f.set('amountRupees')}
          inputMode="decimal"
          aria-invalid={!!f.errors.amountRupees}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-bold"
        />
      </Field>

      <Field label="Paid To" optional>
        <input
          value={f.form.paid_to}
          onChange={f.set('paid_to')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Bill / Voucher No." optional span2>
          <input
            value={f.form.bill_no}
            onChange={f.set('bill_no')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
          />
        </Field>
      </div>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}

/* ------------------------------- Transfer Form ------------------------------- */

function TransferForm({ projectId, onClose, title, hint, existing, onDelete }: FormProps) {
  const { data: lk, isLoading } = useLookups();
  const add = useAddTransfer(projectId);
  const update = useUpdateEntry(projectId);
  const f = useEntryForm(
    existing
      ? {
          date: str(existing.transferred_on),
          to_project_id: str(existing.to_project_id),
          material_id: str(existing.material_id),
          unit_id: str(existing.unit_id),
          quantity: str(existing.quantity),
          rateRupees: existing.rate_paise != null ? rupees(existing.rate_paise) : '',
          vehicle_no: str(existing.vehicle_no),
          driver_name: str(existing.driver_name),
          remarks: str(existing.remarks),
        }
      : {
          date: todayLocal(),
          to_project_id: '',
          material_id: '',
          unit_id: '',
          quantity: '',
          rateRupees: '',
          vehicle_no: '',
          driver_name: '',
          remarks: '',
        },
    (v) => {
      const e: Record<string, string> = {};
      if (!v.to_project_id) e.to_project_id = 'Select the receiving site';
      else if (v.to_project_id === projectId) e.to_project_id = 'Choose a different site';
      if (!v.material_id) e.material_id = 'Select a material';
      if (!v.quantity || Number(v.quantity) <= 0) e.quantity = 'Enter a quantity';
      if (v.rateRupees && !MONEY.test(cleanMoney(v.rateRupees))) {
        e.rateRupees = 'Enter a valid rate';
      }
      return e;
    },
    (v) => {
      const values = {
        ...v,
        rateRupees: v.rateRupees ? cleanMoney(v.rateRupees) : '',
      };
      return existing
        ? update.mutateAsync({ kind: 'transfer', id: existing.id, v: values })
        : add.mutateAsync(values);
    },
    onClose,
    'Transfer recorded',
    !!existing,
  );

  const destinations = lk?.projects.filter((p) => p.id !== projectId);

  const qty = Number(f.form.quantity);
  const rate = Number(cleanMoney(f.form.rateRupees));
  const total =
    Number.isFinite(qty) && Number.isFinite(rate) && qty > 0 && rate > 0
      ? (qty * rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : null;

  return (
    <Wrap
      title={title}
      hint={hint}
      onClose={onClose}
      onSubmit={f.onSubmit}
      busy={f.busy}
      editing={!!existing}
      onDelete={onDelete}
    >
      <Field label="Transfer Date" error={f.errors.date}>
        <input
          type="date"
          value={f.form.date}
          onChange={f.set('date')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <Field label="Receiving Site (To)" error={f.errors.to_project_id}>
        <LookupSelect
          value={f.form.to_project_id}
          onChange={f.set('to_project_id')}
          options={destinations}
          placeholder="Choose receiving site"
          invalid={!!f.errors.to_project_id}
          disabled={isLoading}
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Material" error={f.errors.material_id} span2>
          <LookupSelect
            value={f.form.material_id}
            onChange={(e) => {
              const val = e.target.value;
              f.set('material_id')(e);
              if (val) {
                const mat = lk?.materials.find((m) => m.id === val);
                if (mat) {
                  f.setForm((prev) => ({
                    ...prev,
                    material_id: val,
                    unit_id: mat.unit_id || prev.unit_id,
                    rateRupees: prev.rateRupees || (mat.rate_paise ? (mat.rate_paise / 100).toString() : prev.rateRupees),
                  }));
                }
              }
            }}
            options={lk?.materials}
            placeholder="Choose a material"
            invalid={!!f.errors.material_id}
            disabled={isLoading}
          />
        </Field>
      </div>

      <Field label="Quantity" error={f.errors.quantity}>
        <input
          value={f.form.quantity}
          onChange={f.set('quantity')}
          inputMode="decimal"
          aria-invalid={!!f.errors.quantity}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
        />
      </Field>

      <Field label="Unit" optional>
        <LookupSelect
          value={f.form.unit_id}
          onChange={f.set('unit_id')}
          options={lk?.units}
          placeholder="Choose a unit"
          disabled={isLoading}
        />
      </Field>

      <Field label="Rate (₹ / unit)" optional error={f.errors.rateRupees} hint="Market rate per unit">
        <input
          value={f.form.rateRupees}
          onChange={f.set('rateRupees')}
          inputMode="decimal"
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
        />
      </Field>

      <Field label="Total Transfer Value (₹)" optional hint="Auto-calculated (Qty × Rate)">
        <div className="w-full px-3 py-2 text-xs sm:text-sm bg-emerald-50/50 border border-emerald-200/80 rounded-[8px] text-emerald-800 font-mono font-semibold flex items-center justify-between">
          <span>{total ? `₹${total}` : '₹0'}</span>
          <span className="text-[11px] font-normal text-emerald-600">
            {qty > 0 && rate > 0 ? `${qty} × ₹${f.form.rateRupees}` : 'Enter rate'}
          </span>
        </div>
      </Field>

      <Field label="Vehicle Number" optional>
        <input
          value={f.form.vehicle_no}
          onChange={f.set('vehicle_no')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
        />
      </Field>

      <Field label="Driver Name" optional>
        <input
          value={f.form.driver_name}
          onChange={f.set('driver_name')}
          className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
        />
      </Field>

      <div className="col-span-1 sm:col-span-2">
        <Field label="Remarks" optional span2>
          <textarea
            rows={2}
            value={f.form.remarks}
            onChange={f.set('remarks')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>
      </div>
    </Wrap>
  );
}
